import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Trash2 } from 'lucide-react';
import { DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../../lib/messages';
import type { TargetingClient } from '../../../../../targeting/api';
import {
  CustomTargetingExtField,
  CustomTargetingExtRuleOperator,
} from '../../../../../targeting/customTargetingExtModel';
import type { MadBuyLineItemGeneralForm } from '../../../lineItemsModel';
import { CustomTargetingRuleValueSelect } from './CustomTargetingRuleValueSelect';
import { customTargetingRulePath } from './paths';

export function CustomTargetingExtRule({
  client,
  groupIndex,
  ruleIndex,
  canRemove,
  onRemove,
}: {
  client: TargetingClient;
  groupIndex: number;
  ruleIndex: number;
  canRemove: boolean;
  onRemove: () => void;
}) {
  'use no memo';

  const { control, setValue } = useFormContext<MadBuyLineItemGeneralForm>();
  const prefix = customTargetingRulePath(groupIndex, ruleIndex);
  const selectedField = useWatch({ control, name: `${prefix}.key` });
  const fieldLabel = dashboardT('madBuy.lineItems.customTargetingField');
  const operatorLabel = dashboardT('madBuy.lineItems.customTargetingOperator');

  return (
    <div className="grid gap-3 border-t pt-4 md:grid-cols-[minmax(0,12rem)_minmax(0,12rem)_1fr_auto]">
      <DashboardPageFormField label={fieldLabel}>
        <Controller
          control={control}
          name={`${prefix}.key`}
          render={({ field }) => (
            <Select
              value={field.value}
              onValueChange={(next) => {
                field.onChange(next);
                setValue(`${prefix}.values`, [], { shouldDirty: true, shouldValidate: false });
              }}
            >
              <SelectTrigger aria-label={fieldLabel}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[CustomTargetingExtField.Publisher, CustomTargetingExtField.Placement].map(
                  (value) => (
                    <SelectItem key={value} value={value}>
                      {dashboardT(`madBuy.lineItems.customTargetingFields.${value}`)}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          )}
        />
      </DashboardPageFormField>
      <DashboardPageFormField label={operatorLabel}>
        <Controller
          control={control}
          name={`${prefix}.operator`}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger aria-label={operatorLabel}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(CustomTargetingExtRuleOperator).map((value) => (
                  <SelectItem key={value} value={value}>
                    {dashboardT(`madBuy.lineItems.customTargetingOperators.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </DashboardPageFormField>
      <CustomTargetingRuleValueSelect
        key={selectedField}
        client={client}
        groupIndex={groupIndex}
        ruleIndex={ruleIndex}
      />
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="md:mt-[25px] md:self-start"
        disabled={!canRemove}
        aria-label={dashboardT('madBuy.lineItems.customTargetingRemoveRule')}
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
